import axios from "axios";
import { BASEURL } from "../Constants";

const CORRELATION_ID_STORAGE_KEY = "x-correlation-id";
let inMemoryCorrelationId = null;

const generateRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readStoredCorrelationId = () => {
  if (inMemoryCorrelationId) {
    return inMemoryCorrelationId;
  }

  if (typeof window !== "undefined" && window.sessionStorage) {
    const storedValue = window.sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY);
    if (storedValue) {
      inMemoryCorrelationId = storedValue;
      return storedValue;
    }
  }

  const created = generateRequestId();
  inMemoryCorrelationId = created;

  if (typeof window !== "undefined" && window.sessionStorage) {
    window.sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, created);
  }

  return created;
};

const syncCorrelationIdFromHeaders = (headers) => {
  const correlationId =
    headers?.["x-correlation-id"] ||
    headers?.["X-Correlation-Id"] ||
    headers?.["x-correlationid"];

  if (!correlationId || typeof correlationId !== "string") {
    return;
  }

  inMemoryCorrelationId = correlationId;
  if (typeof window !== "undefined" && window.sessionStorage) {
    window.sessionStorage.setItem(CORRELATION_ID_STORAGE_KEY, correlationId);
  }
};

const parseRetryAfterSeconds = (retryAfterValue) => {
  if (!retryAfterValue) {
    return null;
  }

  const numericValue = Number(retryAfterValue);
  if (!Number.isNaN(numericValue) && numericValue >= 0) {
    return Math.ceil(numericValue);
  }

  const retryDate = new Date(retryAfterValue);
  if (!Number.isNaN(retryDate.getTime())) {
    const diffSeconds = Math.ceil((retryDate.getTime() - Date.now()) / 1000);
    return diffSeconds > 0 ? diffSeconds : 0;
  }

  return null;
};

const normalizeRateLimitError = (error) => {
  const responseHeaders = error?.response?.headers || {};
  const retryAfterRaw =
    responseHeaders["retry-after"] ??
    responseHeaders["ratelimit-reset"] ??
    responseHeaders["x-ratelimit-reset"];
  const retryAfterSeconds = parseRetryAfterSeconds(retryAfterRaw);
  const message =
    retryAfterSeconds !== null
      ? `Too many requests. Please try again in ${retryAfterSeconds} seconds.`
      : "Too many requests. Please try again later.";

  error.message = message;
  error.isRateLimited = true;
  error.retryAfterSeconds = retryAfterSeconds;

  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object") {
    error.response.data = { ...responseData, success: false, message };
  } else if (error?.response) {
    error.response.data = { success: false, message };
  }

  return error;
};

// Create axios instances without token in headers (will be added via interceptor)
export const axiosConfig = axios.create({
  baseURL: BASEURL,
  headers: {
    "Cache-Control": "no-cache",
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

export const axiosFormConfig = axios.create({
  baseURL: BASEURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
});

// Request interceptor to add token dynamically
axiosConfig.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    const token = window.sessionStorage.getItem("token");
    const requestId = generateRequestId();
    const correlationId = readStoredCorrelationId();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["x-request-id"] = requestId;
    config.headers["x-correlation-id"] = correlationId;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosFormConfig.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    const token = window.sessionStorage.getItem("token");
    const requestId = generateRequestId();
    const correlationId = readStoredCorrelationId();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["x-request-id"] = requestId;
    config.headers["x-correlation-id"] = correlationId;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosConfig.interceptors.response.use(
  (response) => {
    syncCorrelationIdFromHeaders(response.headers);
    return response;
  },
  (error) => {
    syncCorrelationIdFromHeaders(error?.response?.headers);

    if (error.response?.status === 429) {
      return Promise.reject(normalizeRateLimitError(error));
    }

    // Handle 401 Unauthorized - redirect to login
    // But don't redirect if it's a dev token (development mode)
    const token = window.sessionStorage?.getItem("token");
    if (error.response?.status === 401 && token && !token.startsWith('dev-token-')) {
      window.sessionStorage.removeItem("token");
      window.sessionStorage.removeItem("showSidebar");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const FORMPOST = axiosFormConfig.post;
export const POST = axiosConfig.post;
export const PUT = axiosConfig.put;
export const GET = axiosConfig.get;
export const DELETE = axiosConfig.delete;

// Export httpServices object for convenience
export const httpServices = {
  get: axiosConfig.get.bind(axiosConfig),
  post: axiosConfig.post.bind(axiosConfig),
  put: axiosConfig.put.bind(axiosConfig),
  delete: axiosConfig.delete.bind(axiosConfig),
  patch: axiosConfig.patch?.bind(axiosConfig) || axiosConfig.put.bind(axiosConfig),
};
